import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { MaskLine, EASE, scrollToWaitlist } from "@/components/landing/Reveal";
import { DEMO_URL } from "@/config";

const BIBLE_SHOT =
  "https://static.prod-images.emergentagent.com/jobs/28ad20f8-a5a1-42d8-8b41-1b8aa783b0db/images/a163ab41e3cd0b62dcbe72ec00272fe009f78970767e29e541fd96584b653027.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <section ref={ref} id="top" className="relative pt-28 md:pt-36 pb-14 md:pb-20 overflow-hidden">
      {/* soft ambient blooms */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[38rem] h-[38rem] rounded-full bg-green/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 right-0 w-[26rem] h-[26rem] rounded-full bg-plum/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-paperwhite/60 px-4 py-1.5 text-xs md:text-sm font-medium text-ink/70 mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-green" strokeWidth={2} />
            Formation infrastructure for Christian communities
          </motion.div>

          <h1 className="font-serif font-semibold tracking-tight text-ink text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-[5rem] lg:leading-[0.98]">
            <MaskLine delay={0.25}>The community platform</MaskLine>
            <MaskLine delay={0.4}>
              built <span className="italic text-green-dark">for disciple-makers.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.75 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-ink/70 leading-relaxed text-balance"
          >
            Community, courses, Bible study, notes and prayer journal, and
            goal tracker in one app, designed for spiritual formation, not
            algorithmic distraction.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
          >
            <button
              onClick={scrollToWaitlist}
              data-testid="hero-waitlist-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-base font-semibold text-sage hover:bg-green-dark transition-colors duration-300"
            >
              Join the waitlist
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </button>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-demo-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-base font-semibold text-ink hover:border-ink/50 hover:bg-paperwhite/50 transition-colors duration-300"
            >
              Try the demo
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 1.1 }}
            className="mt-8 text-sm text-ink/55"
            data-testid="hero-social-proof"
          >
            Nearly 100 leaders, teachers, and creators have already expressed
            interest in Word Amp.
          </motion.p>
        </div>

        {/* Parallax device */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            style={{ y, rotate }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
            className="relative w-[240px] sm:w-[300px]"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-green/10 blur-2xl" />
            <div className="relative rounded-[2.25rem] border-[6px] border-ink bg-ink p-1 shadow-2xl">
              <img
                src={BIBLE_SHOT}
                alt="Word Amp Bible reader with Greek word study"
                className="w-full rounded-[1.85rem] object-cover"
                loading="eager"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
